import React, { useState } from "react";
import "../css/contactus.scss";

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    businessName: "",
    email: "",
    subject: "",
    projectDescription: "",
    businessDuration: "",
    website: "",
    services: {
      brandIdentity: false,
      printCollateral: false,
      packagingDesign: false,
      illustration: false,
      website: false,
      other: false,
    },
    otherNeeds: "",
    budget: "",
    launchDate: "",
    location: "",
    discovery: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        [name]: checked,
      },
    }));
    if (errors.services) {
      setErrors((prev) => ({ ...prev, services: "" }));
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.projectDescription.trim()) {
      newErrors.projectDescription = "Project Description is required";
    }
    if (!formData.businessDuration) {
      newErrors.businessDuration = "Please select how long you have had this business";
    }
    
    const hasSelectedService = Object.values(formData.services).some(value => value === true);
    if (!hasSelectedService) {
      newErrors.services = "Please select at least one service";
    }

    if (!formData.launchDate) {
      newErrors.launchDate = "Ideal Launch Date is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      const firstErrorField = Object.keys(formErrors)[0];
      const element = document.getElementById(firstErrorField) || document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    setTimeout(() => {
      setShowSuccess(true);
      setFormData({
        name: "",
        businessName: "",
        email: "",
        subject: "",
        projectDescription: "",
        businessDuration: "",
        website: "",
        services: {
          brandIdentity: false,
          printCollateral: false,
          packagingDesign: false,
          illustration: false,
          website: false,
          other: false,
        },
        otherNeeds: "",
        budget: "",
        launchDate: "",
        location: "",
        discovery: "",
      });
      setIsSubmitting(false);

      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => setShowSuccess(false), 5000);
    }, 1000);
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <h1 className="contact-title">brand identity questionnaire</h1>

        {showSuccess && (
          <div className="success-message">
            Thank you! Your questionnaire has been submitted successfully.
          </div>
        )}

        <form onSubmit={handleSubmit} className="contact-form">
          <div className="form-group">
            <label htmlFor="name">Name (required)</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? "error-input" : ""}
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="businessName">Business Name</label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address (required)</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? "error-input" : ""}
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="projectDescription">Project Description (required)</label>
            <span className="sub-label">
              Tell us about your jewellery question or what you need help with from pmistpearl.
            </span>
            <textarea
              id="projectDescription"
              name="projectDescription"
              rows="4"
              value={formData.projectDescription}
              onChange={handleInputChange}
              className={errors.projectDescription ? "error-input" : ""}
            ></textarea>
            {errors.projectDescription && <span className="error-text">{errors.projectDescription}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="businessDuration">How long have you had this business? (required)</label>
            <div className="select-wrapper">
              <select
                id="businessDuration"
                name="businessDuration"
                value={formData.businessDuration}
                onChange={handleInputChange}
                className={errors.businessDuration ? "error-input" : ""}
              >
                <option value="">Select an option</option>
                <option value="Less than 1 year">Less than 1 year</option>
                <option value="1-3 years">1-3 years</option>
                <option value="3-5 years">3-5 years</option>
                <option value="5+ years">5+ years</option>
              </select>
            </div>
            {errors.businessDuration && <span className="error-text">{errors.businessDuration}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="website">Website</label>
            <span className="sub-label">Current website or inspiration board</span>
            <input
              type="text"
              id="website"
              name="website"
              placeholder="http://"
              value={formData.website}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group" id="services">
            <label>What service(s) are you interested in? (required)</label>
            <span className="sub-label">Select all that apply.</span>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input type="checkbox" name="brandIdentity" checked={formData.services.brandIdentity} onChange={handleCheckboxChange} />
                <span className="checkmark"></span>
                Brand Identity
              </label>
              <label className="checkbox-label">
                <input type="checkbox" name="printCollateral" checked={formData.services.printCollateral} onChange={handleCheckboxChange} />
                <span className="checkmark"></span>
                Print Collateral
              </label>
              <label className="checkbox-label">
                <input type="checkbox" name="packagingDesign" checked={formData.services.packagingDesign} onChange={handleCheckboxChange} />
                <span className="checkmark"></span>
                Packaging Design
              </label>
              <label className="checkbox-label">
                <input type="checkbox" name="illustration" checked={formData.services.illustration} onChange={handleCheckboxChange} />
                <span className="checkmark"></span>
                Illustration
              </label>
              <label className="checkbox-label">
                <input type="checkbox" name="website" checked={formData.services.website} onChange={handleCheckboxChange} />
                <span className="checkmark"></span>
                Website
              </label>
              <label className="checkbox-label">
                <input type="checkbox" name="other" checked={formData.services.other} onChange={handleCheckboxChange} />
                <span className="checkmark"></span>
                Other (see below)
              </label>
            </div>
            {errors.services && <span className="error-text">{errors.services}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="otherNeeds">Please elaborate on what other needs we can help you with.</label>
            <textarea
              id="otherNeeds"
              name="otherNeeds"
              rows="3"
              value={formData.otherNeeds}
              onChange={handleInputChange}
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="budget">Estimated Project Budget</label>
            <div className="select-wrapper">
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
              >
                <option value="">Select an option</option>
                <option value="Under $1,000">Under $1,000</option>
                <option value="$1,000 - $3,000">$1,000 - $3,000</option>
                <option value="$3,000 - $5,000">$3,000 - $5,000</option>
                <option value="$5,000+">$5,000+</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="launchDate">Ideal Launch Date (required)</label>
            <input
              type="date"
              id="launchDate"
              name="launchDate"
              value={formData.launchDate}
              onChange={handleInputChange}
              className={errors.launchDate ? "error-input" : ""}
            />
            {errors.launchDate && <span className="error-text">{errors.launchDate}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="location">Where are you located?</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="discovery">How did you discover pmistpearl?</label>
            <input
              type="text"
              id="discovery"
              name="discovery"
              value={formData.discovery}
              onChange={handleInputChange}
            />
          </div>

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? "SENDING..." : "SEND"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Contact;
